import React, { useState, useEffect, useRef } from 'react';
import { db, BACKEND_URL } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ArrowLeft, Printer, Download, Upload, Camera, Trash2, Edit } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useHiddenStudentRoute } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

function StudentProfile() {
  const { rollNumber, navigate } = useHiddenStudentRoute('/student/profile');
  const profileRef = useRef();
  const fileInputRef = useRef();
  
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadStudentData();
  }, [rollNumber]);

  const loadStudentData = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        setStudent(studentDoc.data());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading student data:', error);
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('rollNumber', rollNumber); // Roll number FIRST
      formData.append('category', 'photos'); // Category determines folder
      formData.append('file', file); // Changed from 'photo' to 'file' to match backend
      
      // Include old photo URL so backend can delete it
      if (student.photoPath) {
        formData.append('oldPhotoPath', student.photoPath);
      } else if (student.photoUrl) {
        formData.append('oldPhotoUrl', student.photoUrl);
      }

      const response = await fetch(`${BACKEND_URL}/api/upload/photo`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.photoUrl) {
        // Update Firestore with new photo URL
        const studentRef = doc(db, 'students', rollNumber);
        await updateDoc(studentRef, {
          photoUrl: result.photoUrl,
          photoPath: result.photoPath || null,
          photoUpdatedAt: new Date()
        });

        // Update local state immediately
        setStudent(prev => ({
          ...prev,
          photoUrl: result.photoUrl,
          photoPath: result.photoPath || null
        }));

        alert('✓ Photo uploaded successfully!');
        setShowUploadModal(false);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    }
    
    setUploading(false);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // First, delete the physical file from server
      const deleteResponse = await fetch(`${BACKEND_URL}/api/upload/photo/${rollNumber}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photoPath: student.photoPath,
          photoUrl: student.photoUrl
        })
      });

      const deleteResult = await deleteResponse.json();

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Failed to delete photo file');
      }

      console.log('✓ Photo file deleted from server');

      // Then, update Firestore to remove photo URL
      const studentRef = doc(db, 'students', rollNumber);
      await updateDoc(studentRef, {
        photoUrl: null,
        photoPath: null,
        photoDeletedAt: new Date()
      });

      // Update local state immediately
      setStudent(prev => ({
        ...prev,
        photoUrl: null,
        photoPath: null
      }));

      alert('✓ Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error: ' + error.message);
    }
  };


  const handlePrint = () => window.print();

  const handleSavePDF = async () => {
    const element = profileRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = 210;
    const pdfHeight = 297;
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`${rollNumber}_profile.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Student not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 print:bg-white print:py-0">
      <div className="max-w-[210mm] mx-auto px-4 print:px-0">
        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-4 print:hidden">
          <button 
            onClick={() => navigate('/student/dashboard')} 
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex space-x-3">
            <button 
              onClick={handlePrint} 
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button 
              onClick={handleSavePDF} 
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              <span>Save PDF</span>
            </button>
          </div>
        </div>

        {/* A4 Paper Container */}
        <div 
          ref={profileRef} 
          className="bg-white shadow-lg print:shadow-none"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '10mm',
            margin: '0 auto'
          }}
        >
          {/* Header with Letterhead */}
          <div className="mb-3">
            {/* Letterhead Image */}
            <div className="mb-2">
              <img 
                src="/ybit-letterhead.png" 
                alt="YBIT Letterhead" 
                className="w-full h-auto object-contain"
                style={{ maxHeight: '100px' }}
                onError={(e) => {
                  console.error('Letterhead failed to load');
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Divider */}
            <div className="border-b-2 border-black mb-2"></div>
            
            {/* Form Title - H2 */}
            <h2 style={{ fontSize: '14px' }} className="font-bold uppercase text-center mb-1">
              Student Mentoring Form
            </h2>
            
            {/* Department - H3 */}
            <h3 style={{ fontSize: '11px' }} className="text-gray-700 font-medium text-center mb-2">
              Engineering Sciences & Humanities Department - First Year Engineering
            </h3>
          </div>

          {/* Section Header */}
          <div className="bg-black text-white px-2 py-1 mb-2">
            <h2 className="text-xs font-bold uppercase">Student's Personal Details</h2>
          </div>

          {/* Main Content: Details + Photo */}
          <div className="grid grid-cols-4 gap-2 mb-2">
            {/* Left 3 columns - Details */}
            <div className="col-span-3 space-y-2">
              {/* Name Row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Surname</label>
                  <p className="text-xs">{student.surname || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">First Name</label>
                  <p className="text-xs">{student.firstName || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Father's Name</label>
                  <p className="text-xs">{student.fatherName || '-'}</p>
                </div>
              </div>

              {/* DOB & Gender */}
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Date of Birth</label>
                  <p className="text-xs">{student.dob || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Gender</label>
                  <p className="text-xs">{student.gender || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Roll Number</label>
                  <p className="text-xs">{student.rollNumber || rollNumber || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Division</label>
                  <p className="text-xs">{student.division || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Batch</label>
                  <p className="text-xs">{student.batch || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Office</label>
                  <p className="text-xs">{student.officeLabel || '-'}</p>
                </div>
              </div>

              {/* Current Address */}
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Current Address</label>
                <p className="text-xs">{student.currentAddress || '-'}</p>
              </div>

              {/* Permanent Address */}
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Permanent Address</label>
                <p className="text-xs">{student.permanentAddress || '-'}</p>
              </div>

              {/* Aadhar & Blood Group */}
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Aadhar Card Number</label>
                  <p className="text-xs">{student.aadharCard || '-'}</p>
                </div>
                <div className="border border-black p-1.5">
                  <label className="text-[10px] font-semibold block">Blood Group</label>
                  <p className="text-xs">{student.bloodGroup || '-'}</p>
                </div>
              </div>
            </div>

            {/* Right 1 column - Photo */}
            <div className="col-span-1">
              <div 
                className="border-2 border-black relative bg-gray-50 group"
                style={{ width: '100%', height: '260px' }}
              >
                {getStudentPhotoUrl(student) ? (
                  <>
                    <img 
                     src={getStudentPhotoUrl(student)}  
                      alt="Student" 
                      className="w-full h-full object-cover"
                      onLoad={() => console.log('✅ Photo loaded successfully')}
                      onError={(e) => {
                        console.error('❌ Failed to load photo:', student.photoUrl);
                        e.target.src = '';
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Photo action buttons - shown on hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 print:hidden">
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                        title="Replace Photo"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleDeletePhoto}
                        className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
                        title="Delete Photo"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center justify-center h-full">
                      <Camera className="w-10 h-10 text-gray-400" />
                      <p className="text-[10px] text-gray-500 mt-1">Photo</p>
                    </div>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="absolute bottom-1 right-1 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 print:hidden"
                      title="Upload Photo"
                    >
                      <Upload className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Additional Fields */}
          <div className="space-y-2 mb-2">
            {/* ABC ID */}
            <div className="border border-black p-1.5">
              <label className="text-[10px] font-semibold block">ABC ID</label>
              <p className="text-xs">{student.abcId || '-'}</p>
            </div>

            {/* Hobbies */}
            <div className="border border-black p-1.5">
              <label className="text-[10px] font-semibold block">Personal Hobbies/Interest</label>
              <p className="text-xs">{student.hobbies || '-'}</p>
            </div>

            {/* Mobile & WhatsApp */}
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Mobile Number</label>
                <p className="text-xs">{student.mobile || '-'}</p>
              </div>
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">WhatsApp Number</label>
                <p className="text-xs">{student.whatsapp || '-'}</p>
              </div>
            </div>

            {/* Email */}
            <div className="border border-black p-1.5">
              <label className="text-[10px] font-semibold block">Email ID</label>
              <p className="text-xs">{student.email || '-'}</p>
            </div>
          </div>

          {/* Parent's Details Header */}
          <div className="bg-black text-white px-2 py-1 mb-2 mt-3">
            <h2 className="text-xs font-bold uppercase">Parent's Details</h2>
          </div>

          {/* Father's Details */}
          <div className="space-y-2 mb-2">
            <div className="border border-black p-1.5">
              <label className="text-[10px] font-semibold block">Father's Name</label>
              <p className="text-xs">{student.fatherName || '-'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Mobile Number (WhatsApp)</label>
                <p className="text-xs">{student.fatherMobile || '-'}</p>
              </div>
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Father's Occupation</label>
                <p className="text-xs">{student.fatherOccupation || '-'}</p>
              </div>
            </div>
          </div>

          {/* Mother's Details */}
          <div className="space-y-2 mb-3">
            <div className="border border-black p-1.5">
              <label className="text-[10px] font-semibold block">Mother's Name</label>
              <p className="text-xs">{student.motherName || '-'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Mobile Number (WhatsApp)</label>
                <p className="text-xs">{student.motherMobile || '-'}</p>
              </div>
              <div className="border border-black p-1.5">
                <label className="text-[10px] font-semibold block">Mother's Occupation</label>
                <p className="text-xs">{student.motherOccupation || '-'}</p>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="border-t-2 border-black pt-4 mt-4">
            <div className="text-right">
              <p className="text-xs font-semibold">Student's Signature</p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-[9px] text-gray-600 mt-3 pt-2 border-t border-gray-300">
            <p>Bhonsale Knowledge City, Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi, Dist: Sindhudurg 416510</p>
            <p>DTE CODE: 3470 | MSBTE CODE: 1742</p>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:hidden">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Upload Student Photo</h3>
            <p className="text-sm text-gray-600 mb-4">Select a clear photo (JPG/PNG, max 5MB)</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              {uploading ? '⏳ Uploading...' : '📷 Choose Photo'}
            </button>
            <button
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
              className="w-full py-2 bg-gray-300 rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}

export default StudentProfile;
